/**
 * Shared helpers for the Requests page: synthesize chip objects + a
 * "time left" label from a TempAccessRequest.
 *
 * Two kinds flow through:
 *   - `data-access` — temp IAM policies expire 24h after `policyTimestamp`.
 *     Chips styled DA-blue; user chip carries the IAM `fromBastion` ring.
 *   - `impersonation` — AccessOps-owned (DDB), no TTL. Chips styled Impers-purple;
 *     no expiry badge, no `fromBastion` ring.
 */
import {ChipKind, daysUntil, hoursUntil, nameFromEmail} from './aggregate';
import {TempAccessRequest} from './api';
import {isImpersonationKind} from './grant-view';

const POLICY_TTL_MS = 24 * 60 * 60 * 1000;

export function policyExpiresAtIso(r: TempAccessRequest): string {
    return new Date(Number(r.policyTimestamp) + POLICY_TTL_MS).toISOString();
}

function recipientEmail(r: TempAccessRequest, emailFromLookup?: string): string {
    return r.email ?? emailFromLookup ?? r.username ?? '';
}

/** The history event already carries the per-application kind — pass it through. */
function chipKindOf(r: TempAccessRequest): ChipKind {
    return r.kind;
}

export function makeUserChip(r: TempAccessRequest, email?: string) {
    const isImp = isImpersonationKind(r.kind);
    const expiresAtIso = isImp ? undefined : policyExpiresAtIso(r);
    const display = recipientEmail(r, email);
    return {
        email: display,
        name: nameFromEmail(display),
        kind: chipKindOf(r),
        permanent: isImp,
        daysRemaining: isImp ? Number.POSITIVE_INFINITY : daysUntil(expiresAtIso),
        hoursRemaining: isImp ? Number.POSITIVE_INFINITY : hoursUntil(expiresAtIso),
        fromBastion: !isImp,
        grants: [],
    };
}

export function makeTenantChip(r: TempAccessRequest, name?: string) {
    const isImp = isImpersonationKind(r.kind);
    const expiresAtIso = isImp ? undefined : policyExpiresAtIso(r);
    return {
        resource: r.shardId,
        resourceName: name,
        resourceIds: [r.shardId],
        kind: chipKindOf(r),
        permanent: isImp,
        daysRemaining: isImp ? Number.POSITIVE_INFINITY : daysUntil(expiresAtIso),
        hoursRemaining: isImp ? Number.POSITIVE_INFINITY : hoursUntil(expiresAtIso),
        fromBastion: false,
        grants: [],
    };
}

/**
 * Label for the "Time left" column. Impersonation grants are permanent and
 * render `∞`; DA grants count down from a 24h TTL anchored at policyTimestamp.
 */
export function timeLeftLabel(r: TempAccessRequest): string {
    if (isImpersonationKind(r.kind)) {
        return '∞';
    }
    const ms = Number(r.policyTimestamp) + POLICY_TTL_MS - Date.now();
    if (ms <= 0) {
        return '—';
    }
    const hours = Math.floor(ms / 3_600_000);
    if (hours >= 1) {
        return `${hours}h`;
    }
    return `${Math.floor(ms / 60_000)}m`;
}
